import React, { useState, useEffect, useRef, createContext } from 'react';
import { Outlet, NavLink, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import {
  Wine, ShoppingCart, Package, ClipboardList, User, LogOut,
  MessageSquare, Send, CheckCircle, House, LayoutDashboard,
  Eye, X,
} from 'lucide-react';
import { wholesaleCustomers } from '../services/api';
import '../styles/storefront.css';

// ── Hero page context ─────────────────────────────────────────────────────────
// Child pages that render a full-bleed hero banner opt in like this:
//   const setIsHeroPage = useContext(HeroPageContext);
//   useEffect(() => { setIsHeroPage(true); return () => setIsHeroPage(false); }, [setIsHeroPage]);
// This makes the navbar transparent when the page is scrolled to the top of
// the hero, then transitions to an opaque dark bar as the user scrolls down.
export const HeroPageContext = createContext(() => {});

export default function CustomerLayout() {
  const { user, logout, isImpersonating, restoreAdminSession } = useAuth();
  const { cartCount } = useCart();
  const navigate = useNavigate();

  // Impersonation data from localStorage (set by CustomerDetail before redirect)
  const impData = isImpersonating
    ? (() => { try { return JSON.parse(localStorage.getItem('isImpersonating')); } catch { return null; } })()
    : null;

  // Countdown timer
  const [secondsLeft, setSecondsLeft] = useState(() => {
    if (!impData) return 900;
    if (impData.expiresAt) return Math.max(0, Math.round((impData.expiresAt - Date.now()) / 1000));
    return impData.expiresIn || 900;
  });

  useEffect(() => {
    if (!isImpersonating) return;
    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          restoreAdminSession();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [isImpersonating, restoreAdminSession]);

  const formatCountdown = (s) => {
    const m   = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  // ── Hero navbar transparency ─────────────────────────────────────────────────
  // isHeroPage is set to true by child pages that call setIsHeroPage via context.
  // When true and scroll < 80px, the navbar becomes transparent and floats over
  // the hero image. Past 80px it snaps back to the solid dark bar.
  const [isHeroPage, setIsHeroPage] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    if (!isHeroPage) {
      nav.classList.remove('sf-navbar--transparent');
      return;
    }

    const updateTransparency = () => {
      if (window.scrollY < 80) {
        nav.classList.add('sf-navbar--transparent');
      } else {
        nav.classList.remove('sf-navbar--transparent');
      }
    };

    updateTransparency(); // apply immediately on mount
    window.addEventListener('scroll', updateTransparency, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateTransparency);
      nav.classList.remove('sf-navbar--transparent');
    };
  }, [isHeroPage]);

  // ── Staff preview banner ─────────────────────────────────────────────────────
  const [previewDismissed, setPreviewDismissed] = useState(false);

  // ── Announcement banner ──────────────────────────────────────────────────────
  const [announcement, setAnnouncement] = useState('');
  useEffect(() => {
    wholesaleCustomers.getAnnouncement()
      .then(r => setAnnouncement(r.data?.announcement || ''))
      .catch(() => {});
  }, []);

  // ── Message modal state ────────────────────────────────────────────────────
  const [showMsgModal, setShowMsgModal] = useState(false);
  const [msgProfile, setMsgProfile]     = useState(null);
  const [msgProfileLoading, setMsgProfileLoading] = useState(false);
  const [msgSubject, setMsgSubject]     = useState('');
  const [msgBody, setMsgBody]           = useState('');
  const [msgSending, setMsgSending]     = useState(false);
  const [msgError, setMsgError]         = useState('');
  const [msgSent, setMsgSent]           = useState(false);

  const openMsgModal = async () => {
    setShowMsgModal(true);
    if (!msgProfile) {
      setMsgProfileLoading(true);
      try {
        const res = await wholesaleCustomers.getProfile();
        setMsgProfile(res.data.data);
      } catch {
        // silently fail — modal will show fallback
      } finally {
        setMsgProfileLoading(false);
      }
    }
  };

  const closeMsgModal = () => {
    setShowMsgModal(false);
    setMsgSubject('');
    setMsgBody('');
    setMsgError('');
    setMsgSent(false);
  };

  const handleSendMessage = async () => {
    if (!msgSubject.trim() || !msgBody.trim()) {
      setMsgError('Subject and message are required.');
      return;
    }
    setMsgSending(true);
    setMsgError('');
    try {
      await wholesaleCustomers.sendMessage({ subject: msgSubject.trim(), body: msgBody.trim() });
      setMsgSent(true);
    } catch (err) {
      setMsgError(err?.response?.data?.message || 'Failed to send message. Please try again.');
    } finally {
      setMsgSending(false);
    }
  };

  const rep = msgProfile?.rep;

  return (
    <HeroPageContext.Provider value={setIsHeroPage}>
      <div className="d-flex flex-column sf-page">

        {/* ── Staff preview banner (topmost) ── */}
        {(user?.role === 'admin' || user?.role === 'sales_rep') && !isImpersonating && !previewDismissed && (
          <div
            className="d-flex align-items-center justify-content-between px-3 py-2 small"
            style={{ background: '#2c2c2c', color: '#fffbed', zIndex: 1100, position: 'relative' }}
          >
            <span className="d-flex align-items-center gap-2">
              <Eye size={14} />
              Staff preview — you are viewing the customer storefront as{' '}
              <strong>{user.firstName} {user.lastName}</strong>.
              Prices reflect base wholesale pricing.
            </span>
            <button
              className="btn btn-sm p-0 ms-3"
              style={{ color: '#fffbed', opacity: 0.7, lineHeight: 1 }}
              onClick={() => setPreviewDismissed(true)}
              aria-label="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* ── Announcement banner ── */}
        {announcement && (
          <div className="sf-announcement">{announcement}</div>
        )}

        {/* ── Impersonation banner (above nav) ── */}
        {isImpersonating && impData && (
          <div
            className="bg-warning d-flex align-items-center justify-content-between px-3 py-2"
            style={{ fontSize: 13, zIndex: 1050 }}
          >
            <span>
              👁 <strong>Admin Preview</strong>: Viewing portal as{' '}
              <strong>{impData.customerName}</strong>
            </span>
            <div className="d-flex align-items-center gap-3">
              <span className="font-monospace fw-semibold">{formatCountdown(secondsLeft)}</span>
              <button
                className="btn btn-sm btn-dark"
                onClick={restoreAdminSession}
              >
                End Preview
              </button>
            </div>
          </div>
        )}

        {/* ── Top navbar ── */}
        <nav
          ref={navRef}
          className="navbar navbar-expand-lg navbar-dark bg-dark sticky-top shadow-sm sf-navbar"
        >
          <div className="container-fluid px-3">

            {/* Brand */}
            <Link className="navbar-brand d-flex align-items-center gap-2 me-4" to="/wholesale/home">
              <Wine size={20} />
              <span className="fw-semibold" style={{ fontSize: 15 }}>Jeremy Wine Co.</span>
            </Link>

            {/* Mobile toggler */}
            <button
              className="navbar-toggler border-0 p-1"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#customerNav"
              aria-controls="customerNav"
            >
              <span className="navbar-toggler-icon" />
            </button>

            <div className="collapse navbar-collapse" id="customerNav">
              {/* Primary links */}
              <ul className="navbar-nav me-auto mb-2 mb-lg-0">
                <li className="nav-item">
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link d-flex align-items-center gap-1 ${isActive ? 'active' : ''}`
                    }
                    to="/wholesale/home"
                  >
                    <House size={15} /> Home
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link d-flex align-items-center gap-1 ${isActive ? 'active' : ''}`
                    }
                    to="/wholesale/catalog"
                  >
                    <Package size={15} /> Catalog
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link d-flex align-items-center gap-1 ${isActive ? 'active' : ''}`
                    }
                    to="/wholesale/orders"
                  >
                    <ClipboardList size={15} /> Orders
                  </NavLink>
                </li>
                <li className="nav-item">
                  <NavLink
                    className={({ isActive }) =>
                      `nav-link d-flex align-items-center gap-1 ${isActive ? 'active' : ''}`
                    }
                    to="/wholesale/account"
                  >
                    <LayoutDashboard size={15} /> Account
                  </NavLink>
                </li>
              </ul>

              {/* Right side */}
              <ul className="navbar-nav ms-auto align-items-center gap-1">
                {/* Message icon — hidden during impersonation */}
                {!isImpersonating && (
                  <li className="nav-item me-1">
                    <button
                      className="nav-link btn btn-link px-2 text-white-50 border-0"
                      onClick={openMsgModal}
                      title="Contact Your Rep"
                      style={{ background: 'none' }}
                    >
                      <MessageSquare size={18} />
                    </button>
                  </li>
                )}

                {/* Cart */}
                <li className="nav-item me-1">
                  <Link className="nav-link position-relative px-2" to="/wholesale/checkout">
                    <ShoppingCart size={18} />
                    {cartCount > 0 && (
                      <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill sf-cart-badge">
                        {cartCount}
                      </span>
                    )}
                  </Link>
                </li>

                {/* User dropdown */}
                <li className="nav-item dropdown">
                  <button
                    className="nav-link dropdown-toggle btn btn-link text-white-50 text-decoration-none d-flex align-items-center gap-1"
                    data-bs-toggle="dropdown"
                    style={{ border: 'none', background: 'none' }}
                  >
                    <User size={16} />
                    <span className="d-none d-lg-inline">{user?.firstName}</span>
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end shadow">
                    <li className="px-3 py-1">
                      <p className="small text-muted mb-0">{user?.firstName} {user?.lastName}</p>
                      <p className="small text-muted mb-0" style={{ fontSize: 11 }}>{user?.email}</p>
                    </li>
                    <li><hr className="dropdown-divider my-1" /></li>
                    <li>
                      <Link className="dropdown-item" to="/wholesale/profile">
                        <User size={13} className="me-2" />Profile
                      </Link>
                    </li>
                    <li>
                      <button
                        className="dropdown-item d-flex align-items-center gap-2 text-danger"
                        onClick={handleLogout}
                      >
                        <LogOut size={13} /> Sign out
                      </button>
                    </li>
                  </ul>
                </li>
              </ul>
            </div>
          </div>
        </nav>

        {/* ── Page content ── */}
        <main className="flex-grow-1">
          <Outlet />
        </main>

        {/* ── Footer ── */}
        <footer
          className="text-white-50 text-center py-4 small"
          style={{ background: 'var(--sf-dark)' }}
        >
          <p className="sf-serif mb-1" style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem' }}>
            Jeremy Wine Co.
          </p>
          <p className="mb-0">
            &copy; {new Date().getFullYear()} Jeremy Wine Co. &mdash; Wholesale Portal
          </p>
        </footer>

        {/* ── Send Message Modal ── */}
        {showMsgModal && (
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            aria-modal="true"
            style={{ background: 'rgba(0,0,0,.5)', zIndex: 1060 }}
            onClick={(e) => { if (e.target === e.currentTarget && !msgSending) closeMsgModal(); }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center gap-2">
                    <Send size={16} /> Contact Your Rep
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeMsgModal}
                    disabled={msgSending}
                  />
                </div>
                <div className="modal-body">
                  {msgProfileLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading…</span>
                      </div>
                    </div>
                  ) : msgSent ? (
                    <div className="text-center py-3">
                      <CheckCircle size={44} className="text-success mb-3" />
                      <p className="fs-5 fw-semibold mb-1">Message sent!</p>
                      <p className="text-muted small mb-0">
                        Your rep will receive your message and can reply directly to your email.
                      </p>
                    </div>
                  ) : !rep ? (
                    <div className="text-center py-3 text-muted">
                      <MessageSquare size={40} className="mb-3 opacity-50" />
                      <p className="mb-0">You don't have an assigned rep yet.</p>
                      <p className="small">Please contact us directly for assistance.</p>
                    </div>
                  ) : (
                    <>
                      {msgError && (
                        <div className="alert alert-danger py-2">{msgError}</div>
                      )}
                      <div className="mb-3">
                        <label className="form-label">To</label>
                        <input
                          type="text"
                          className="form-control"
                          value={`${rep.firstName} ${rep.lastName}`}
                          disabled
                        />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Subject</label>
                        <input
                          type="text"
                          className="form-control"
                          value={msgSubject}
                          onChange={(e) => setMsgSubject(e.target.value)}
                          placeholder="What's this about?"
                          maxLength={255}
                          autoFocus
                        />
                      </div>
                      <div className="mb-0">
                        <label className="form-label">Message</label>
                        <textarea
                          className="form-control"
                          rows={5}
                          value={msgBody}
                          onChange={(e) => setMsgBody(e.target.value)}
                          placeholder="Type your message here…"
                        />
                      </div>
                    </>
                  )}
                </div>
                {!msgSent && !msgProfileLoading && rep && (
                  <div className="modal-footer">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={closeMsgModal}
                      disabled={msgSending}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary d-inline-flex align-items-center gap-2"
                      onClick={handleSendMessage}
                      disabled={msgSending}
                    >
                      {msgSending
                        ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                        : <Send size={14} />
                      }
                      Send
                    </button>
                  </div>
                )}
                {(msgSent || (!msgProfileLoading && !rep)) && (
                  <div className="modal-footer">
                    <button className="btn btn-outline-secondary" onClick={closeMsgModal}>
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </HeroPageContext.Provider>
  );
}
